import React, { useCallback, useState } from "react";
import * as R from "ramda";
import useResizeObserver from "use-resize-observer/polyfilled";
import { FullPageSpinner } from "components/Spinner";
import { Error } from "components/Error";
import {
  useRedirect,
  usePostMessage,
  FLOW_STATE,
  inIframe,
  useSessionCtx,
  useSessionJWT
} from "utils";
import { logError } from "api/accounts-customer";

const getDim = (el) => {
  if (R.isNil(el)) return undefined;
  return {
    height: R.prop("scrollHeight", el),
  };
};

const pre = "[Flow]";

/**
 * FlowStepsHandler
 * This component holds on to the steps the end-user is required to go through before reaching their
 * final step, making sure callbacks are propagated to the window when in an iframe.
 *
 * @param {object} props
 * @param {FlowConfig} props.config
 * @param {string} [props.flow]
 * @param {string[]} [props.steps]
 * @returns JSX.Element
 */
const FlowStepsHandler = ({ config, flow = "_UNSPECIFIED_FLOW_", steps = [] }) => {
  const { debug, sessionCtx } = useSessionCtx();
  const { sessionJWT } = useSessionJWT();
  const [flowReady, setFlowReady] = useState(false);

  const [redirect] = useRedirect();
  const { postOnComplete, postOnReady, postOnError, postOnEvent, postOnUpdate } =
    usePostMessage(flow, sessionCtx?.intCtx);

  const [state, setState] = useState({
    currentStep: 0,
    steps,
    finalStep: steps.length - 1,
    postFlow: false,
    showSpinner: false,
  });
  debug(pre, "state.currentStep: ", state.currentStep);
  const Component = state.steps[state.currentStep];

  const postDim = useCallback(() => {
    if (!inIframe()) return;
    const dim = getDim(flowRef.current);
    debug(pre, "posting resize:", dim);
    postOnEvent({ type: "resize", dim });
  }, [debug, postOnEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  const { ref: flowRef } = useResizeObserver({ onResize: postDim });

  const handleReady = useCallback((data) => {
    debug(pre, "handleReady", data);
    setFlowReady(true);
    postDim();
    inIframe() && postOnReady(data);
  }, [debug, setFlowReady, postOnReady, postDim]);

  const handleUpdate = (data) => {
    debug(pre, "handleUpdate", data);
    inIframe() && postOnUpdate(data);
  };

  const handleError = (error) => {
    debug(pre, "handle_error", error, state.currentStep);
    logError(sessionJWT, {
      flowLoc: `${pre} (${flow}) handle_error`,
      error: error.message,
      currentStep: state.currentStep,
    });
    inIframe() && postOnError(error);
  };

  const returnComplete = useCallback(
    (data) => (inIframe() ? postOnComplete(data) : redirect(data)),
    [postOnComplete, redirect]
  );

  const handleComplete = useCallback(
    (data) => {
      debug(pre, "handle_complete", state.currentStep, data);
      switch (data.state) {
        case FLOW_STATE.COMPLETE:
        case FLOW_STATE.BYPASS:
          debug(pre, "..complete");
          if (state.currentStep === state.finalStep) {
            debug(pre, "final step!");
            returnComplete(data);
            setState({ ...state, postFlow: true, showSpinner: true });
          } else {
            debug(pre, "next step");
            setState({ ...state, currentStep: state.currentStep + 1 });
          }
          break;
        case FLOW_STATE.CANCEL:
          debug(pre, "..cancel");
          returnComplete(data);
          setState({ ...state, postFlow: true, showSpinner: true });
          break;
        case FLOW_STATE.PENDING:
        case FLOW_STATE.DENIED:
          // KYC specific
          // even if this is not the last step in the overall flow, we have to bail
          debug(pre, "..user stuck");
          returnComplete(data);
          setState({ ...state, postFlow: true, showSpinner: false });
          break;
        default:
          debug(pre, "ERROR: unknown state", data);
      }
    },
    [debug, state, returnComplete]
  );

  // we only want to show the spinner in the case of the user being fully done with the overall flow, or if the user cancelled
  if (state.showSpinner) {
    return <FullPageSpinner data-testid="post-flow" />;
  }

  return (
    <div
      style={{
        height: "100%",
      }}
      data-testid="flow"
    >
      {!flowReady && <FullPageSpinner id="FLOW.COMMON" />}
      <div ref={flowRef}>
        {Component ? (
          <Component
            flow={flow}
            config={config}
            onReady={handleReady}
            onUpdate={handleUpdate}
            onComplete={handleComplete}
            onError={handleError}
          />
        ) : (
          <Error />
        )}
      </div>
    </div>
  );
};

export default FlowStepsHandler;
